import { useId, useState, type KeyboardEvent } from 'react';
import {
  describeTagSuggestion,
  matchTagSuggestions,
  normaliseTag,
  type TagSuggestion,
} from '@trailblazer/shared';
import { LIMITS } from '../validation';
import type { UploadOverlayVm } from '../useUploadOverlay';

/**
 * TagsControl — the tag chip input, wearing the Design's field box. THE ONLY COPY: V2 (the hand-composed
 * reskin) and V3 (the designer's pages) both render this one, and V1's classic field shares its
 * behaviour through `matchTagSuggestions`.
 *
 * WHY A CHIP INPUT AND NOT A TEXT BOX. The frame draws Tags as one plain field, and a single text box
 * is the obvious copy of that — but `DraftDetails.tags` is a `string[]` with real bounds the server
 * enforces (`maxTags` 5, `maxTagLength` 60, CAT-F12 `tool_tag_cap`). A comma-splitting text box cannot
 * express a cap: it lets the builder type a seventh tag and only finds out at upload, two steps later.
 * So the field keeps the drawn BOX and gains the commit behaviour the data requires — the same trade
 * FormField's `children` slot exists for.
 *
 * ── WHY IT SUGGESTS (CAT-F12) ──
 *
 * Tags are created on first use by whoever types them, and nothing here used to tell the builder what
 * the platform already has. So two people describing the same thing produce 'Power Automate' and
 * 'PowerAutomate', and the owner merges them by hand afterwards (`db/tagAdmin.ts`). The cheapest place
 * to prevent that is the moment before the word is invented, which is this control.
 *
 * The list arrives on `refData` — the call the form already makes and blocks on — so it is filtered in
 * the browser and the first suggestion appears on the FIRST keystroke. A debounced `?q=` round trip to
 * a single-writer PGlite on a Pi would arrive after the builder had already typed their own word and
 * pressed Enter, which is precisely the failure being prevented.
 *
 * MATCHING IS SHARED, NOT LOCAL. `matchTagSuggestions` lives in `@trailblazer/shared` beside the CAT-F11
 * normaliser the server resolves with, so typing 'power_auto' finds 'Power Automate' — a label-substring
 * match would not, because the label has a space where the input has an underscore.
 *
 * TWO THINGS THAT LOOK LIKE POLISH AND ARE NOT:
 *
 *   - `onMouseDown` on each option calls `preventDefault()`. Without it, clicking a suggestion blurs the
 *     input first, `onBlur` commits the HALF-TYPED text as its own tag, and then the click adds the
 *     suggestion — so picking 'Power Automate' after typing 'power' silently produces two tags and
 *     consumes two of the five.
 *   - The panel is only rendered while the field has focus. It sits directly above the Next button in a
 *     modal; left open, it covers the control the builder is reaching for.
 *
 * The backend remains the authority; everything here is a UX guard, not validation.
 */
export function TagsControl({
  details,
  onChange,
  suggestions = [],
}: {
  details: UploadOverlayVm['details'];
  onChange: UploadOverlayVm['onDetailsChange'];
  /**
   * `refData.tags`. Defaults to `[]` so a caller that has not been threaded yet, or a refdata call that
   * failed, renders the field exactly as it behaved before suggestions existed rather than crashing.
   */
  suggestions?: TagSuggestion[];
}) {
  // Controlled, where it used to be uncontrolled: the suggestion list is a function of what has been
  // typed, so the typed text has to be state rather than only living in the DOM node.
  const [typed, setTyped] = useState('');
  const [focused, setFocused] = useState(false);
  /** Keyboard position within the panel. -1 means "still in the input", where Enter commits the text. */
  const [active, setActive] = useState(-1);
  const listId = useId();

  const atCap = details.tags.length >= LIMITS.maxTags;
  const offered = atCap ? [] : matchTagSuggestions(suggestions, typed, details.tags);
  const panelOpen = focused && offered.length > 0;

  function commitTag(raw: string): boolean {
    const label = raw.trim();
    if (label === '' || label.length > LIMITS.maxTagLength) return false;
    if (details.tags.length >= LIMITS.maxTags) return false;
    // Compared on the CAT-F11 key, not the label: 'power automate' and 'Power-Automate' are one tag to
    // the server, so accepting both would send two labels and show one chip on the finished tool.
    const key = normaliseTag(label);
    if (key === '' || details.tags.some((t) => normaliseTag(t) === key)) return false;
    onChange({ tags: [...details.tags, label] });
    return true;
  }

  /** Commit and reset the field — shared by Enter, blur and picking a suggestion. */
  function take(label: string) {
    if (commitTag(label)) {
      setTyped('');
      setActive(-1);
    }
  }

  function onTagKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && offered.length > 0) {
      e.preventDefault();
      setActive((i) => (i + 1) % offered.length);
      return;
    }
    if (e.key === 'ArrowUp' && offered.length > 0) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? offered.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Escape' && panelOpen) {
      // Stopped here: the overlay's own Escape handler closes the whole upload dialog, and a builder
      // dismissing a suggestion panel has not asked to abandon their draft.
      e.preventDefault();
      e.stopPropagation();
      setActive(-1);
      setFocused(false);
      return;
    }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const chosen = active >= 0 ? offered[active] : undefined;
      take(chosen ? chosen.label : typed);
      return;
    }
    if (e.key === 'Backspace' && typed === '' && details.tags.length > 0) {
      onChange({ tags: details.tags.slice(0, -1) });
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-field-gap">
      {details.tags.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {details.tags.map((tag, i) => (
            <li
              key={`${tag}-${i}`}
              className="flex items-center gap-2 rounded-sm bg-accent-subtle px-2 py-1 text-xs font-light text-ink"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                className="text-ink"
                onClick={() => onChange({ tags: details.tags.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative min-w-0">
        <input
          type="text"
          role="combobox"
          aria-expanded={panelOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          {...(panelOpen && active >= 0 ? { 'aria-activedescendant': `${listId}-${active}` } : {})}
          value={typed}
          maxLength={LIMITS.maxTagLength}
          placeholder={atCap ? 'Tag limit reached' : 'Type a tag, then Enter'}
          disabled={atCap}
          onChange={(e) => {
            setTyped(e.target.value);
            setActive(-1);
          }}
          onFocus={() => setFocused(true)}
          onKeyDown={onTagKeyDown}
          onBlur={() => {
            setFocused(false);
            take(typed);
          }}
          className="h-field w-full min-w-0 rounded-md border border-border-subtle bg-transparent px-field-x text-xs font-light text-ink outline-none focus-visible:border-ring-focus"
        />
        {panelOpen && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Tags already used across the catalogue"
            className="absolute left-0 right-0 top-[calc(100%+4px)] z-10 max-h-48 overflow-y-auto rounded-md border border-border-subtle bg-surface py-1 shadow-lg"
          >
            {offered.map((s, i) => (
              <li
                key={s.normalised}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  // See the header: without preventDefault the blur commits the half-typed text too.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => take(s.label)}
                  className={`flex w-full items-center justify-between gap-3 px-field-x py-1 text-left text-xs font-light text-ink ${
                    i === active ? 'bg-accent-subtle' : ''
                  }`}
                >
                  <span className="min-w-0 truncate">{s.label}</span>
                  <span className="shrink-0 text-ink/60">{describeTagSuggestion(s)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
